import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useLogout } from "@/hooks/auth/useLogout";
import { HomeIcon, ArrowLeft, LogOut } from "lucide-react";
import { motion } from "framer-motion";

// Access-denied page, styled like the ErrorPage. Offers a logout escape for
// users whose account genuinely has no viewable page (e.g. staff with no
// access role yet) — for them "Accueil" would just come back here.
const NoAccess: React.FC = () => {
  const navigate = useNavigate();

  // The normal logout: ends the API session and clears the user's state
  // instead of wiping all of localStorage (SFRESTO-164). A full page load then
  // leaves the broken screen behind.
  const logout = useLogout();
  const handleLogoutAndReset = async () => {
    await logout();
    window.location.assign("/auth/login");
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="text-center max-w-3xl mx-auto">
        <motion.h1
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="text-9xl font-bold text-primary mb-2"
        >
          403
        </motion.h1>

        <motion.h2
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="text-2xl font-semibold text-th-gray-22 mb-4"
        >
          Accès refusé
        </motion.h2>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="text-gray-600 mb-20"
        >
          Vous n'avez pas accès à cette page. Si vous pensez que c'est une
          erreur, rapprochez-vous de votre manager.
        </motion.p>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
          className="flex flex-col sm:flex-row gap-3 justify-center"
        >
          <Button
            onClick={() => navigate(-1)}
            variant="outline"
            className="flex items-center gap-2 bg-white hover:bg-gray-100"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour
          </Button>
          <Button
            onClick={() => navigate("/")}
            className="flex items-center gap-2"
          >
            <HomeIcon className="w-4 h-4" />
            Accueil
          </Button>
          <Button
            onClick={handleLogoutAndReset}
            variant="destructive"
            className="flex items-center gap-2"
          >
            <LogOut className="w-4 h-4" />
            Se déconnecter
          </Button>
        </motion.div>
      </div>
    </div>
  );
};

export default NoAccess;
